import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'framer-motion';

// Small, animated table for hero section using data from sample-portfolio-report.xlsx
const PortfolioComparisonTable = () => {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnIndices, setColumnIndices] = useState([]);
  const [ultimateParentIdx, setUltimateParentIdx] = useState(-1);
  const [priceIdx, setPriceIdx] = useState(-1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/sample-portfolio-report.xlsx');
        if (!response.ok) throw new Error('Failed to load portfolio comparison data');

        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (!data || data.length < 2) {
          setRows([]);
          setLoading(false);
          return;
        }

        const allHeaders = data[0].map(String);

        // Identify special columns
        const ultimateIdx = allHeaders.findIndex(h => {
          const lower = h.toLowerCase();
          return (
            lower.includes('ultimateparent') ||
            lower.includes('ultimate_parent') ||
            lower.includes('ultimate parent') ||
            lower.includes('assignee')
          );
        });
        const priceIndex = allHeaders.findIndex(h =>
          h.toLowerCase().includes('price_usd') ||
          h.toLowerCase().includes('price')
        );
        const caIdx = allHeaders.findIndex(
          h => h.trim().toLowerCase() === 'ca'
        );
        const icoIdx = allHeaders.findIndex(
          h => h.trim().toLowerCase() === 'ico'
        );

        // Build list of column indices to display (all except CA and ICO)
        const indices = allHeaders
          .map((_, idx) => idx)
          .filter(idx => idx !== caIdx && idx !== icoIdx);

        const filteredHeaders = indices.map(idx => allHeaders[idx]);

        const bodyRows = data.slice(1).filter(r => r && r.length > 0);

        // Keep just the first 8–10 rows for the hero
        setRows(bodyRows.slice(0, 10));
        setHeaders(filteredHeaders);
        setColumnIndices(indices);
        setUltimateParentIdx(ultimateIdx);
        setPriceIdx(priceIndex);
      } catch (err) {
        console.error('Error loading portfolio comparison table:', err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="w-full max-w-5xl mx-auto mb-8">
        <div className="h-40 rounded-2xl bg-white/80 border border-blue-100 shadow-sm flex items-center justify-center text-gray-500 text-sm">
          Loading sample portfolio comparison…
        </div>
      </div>
    );
  }

  if (!rows.length || !headers.length || !columnIndices.length) {
    return null;
  }

  // For price coloring in hero (gradient green -> yellow -> red)
  const numericPrices = rows
    .map(r => {
      if (priceIdx < 0) return NaN;
      const v = r[priceIdx];
      return typeof v === 'number'
        ? v
        : parseFloat(String(v).replace(/[^0-9.-]/g, ''));
    })
    .filter(v => !isNaN(v));
  const minPrice = numericPrices.length ? Math.min(...numericPrices) : 0;
  const maxPrice = numericPrices.length ? Math.max(...numericPrices) : 0;

  const lerp = (a, b, t) => a + (b - a) * t;
  const lerpColor = (c1, c2, t) => {
    const r = Math.round(lerp(c1[0], c2[0], t));
    const g = Math.round(lerp(c1[1], c2[1], t));
    const b = Math.round(lerp(c1[2], c2[2], t));
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Price heatmap: red (low) -> yellow (mid) -> green (high)
  const getPriceBgStyle = (value) => {
    if (!numericPrices.length) return {};
    const num =
      typeof value === 'number'
        ? value
        : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(num)) return {};

    const tRaw = (num - minPrice) / (maxPrice - minPrice || 1);
    const t = Math.min(1, Math.max(0, tRaw));

    // Tailwind-ish, slightly lighter palette:
    // red-400:    (248, 113, 113)
    // yellow-300: (253, 224, 71)
    // green-400:  (74, 222, 128)
    const RED = [248, 113, 113];
    const YELLOW = [253, 224, 71];
    const GREEN = [74, 222, 128];

    let color;
    if (t <= 0.5) {
      // red -> yellow
      color = lerpColor(RED, YELLOW, t * 2);
    } else {
      // yellow -> green
      color = lerpColor(YELLOW, GREEN, (t - 0.5) * 2);
    }

    return {
      backgroundColor: color,
      color: '#000000',
    };
  };

  const getParentStyle = (value) => {
    const v = String(value || '').toLowerCase();
    if (v.includes('company a')) {
      // Match ExcelViewer: very light blue
      return { backgroundColor: '#dbeafe' };
    }
    if (v.includes('company b')) {
      // Match ExcelViewer: very light green
      return { backgroundColor: '#dcfce7' };
    }
    return {};
  };

  return (
    <div className="w-full mb-8">
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/15 via-indigo-600/15 to-purple-600/15 rounded-2xl blur-3xl" />
        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl border border-blue-100 shadow-xl overflow-hidden">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <div className="text-left">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                Sample Patent Portfolio Valuation
              </p>
              <p className="text-xs text-gray-500">
                Values shown for illustration only
              </p>
            </div>
          </div>
          <div className="overflow-auto">
            <table
              className="min-w-full text-left text-xs sm:text-sm border-t border-gray-200"
              style={{ borderCollapse: 'collapse' }}
            >
              <thead className="bg-gray-900 text-white text-xs uppercase tracking-wide">
                <tr>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="px-3 sm:px-4 py-2 sm:py-3 font-semibold whitespace-nowrap border-b border-gray-700 border-r last:border-r-0"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <AnimatePresence initial={false}>
                <tbody>
                  {rows.map((row, idx) => (
                    <motion.tr
                      key={`${row.ultimateParent}-${idx}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35, delay: idx * 0.03 }}
                    >
                      {columnIndices.map((colIdx, colDisplayIdx) => {
                        const value = row[colIdx];
                        const isParent = colIdx === ultimateParentIdx;
                        const isPrice = colIdx === priceIdx;

                        const parentStyle = isParent ? getParentStyle(value) : {};
                        const priceStyle = isPrice ? getPriceBgStyle(value) : {};
                        const cellStyle =
                          Object.keys(parentStyle).length || Object.keys(priceStyle).length
                            ? { ...parentStyle, ...priceStyle }
                            : undefined;
                        return (
                          <td
                            key={colDisplayIdx}
                            className={`px-3 sm:px-4 py-2 sm:py-2.5 text-gray-900 whitespace-nowrap border-t border-b border-r border-gray-200 last:border-r-0 ${
                              isPrice ? 'font-mono text-xs sm:text-sm' : ''
                            }`}
                            style={cellStyle}
                          >
                            {value !== undefined && value !== null && value !== '' ? String(value) : '-'}
                          </td>
                        );
                      })}
                    </motion.tr>
                  ))}
                </tbody>
              </AnimatePresence>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioComparisonTable;


